/**
 * Reducer Handler
 */
import { BinInfoResponse } from "@kushki/js/lib/types/bin_info_response";
import { GetBrandsLogosByMerchantResponse } from "@kushki/js/lib/types/get_brands_logos_by_merchant_response";
import {
  CardAction,
  IDeferredOptionsFetch,
} from "containers/Card/store/CardAction";
import { CardState, ICommissionConfig } from "containers/Card/store/CardState";
import { IInputProps, ISelectProps } from "root/store/CajitaStore";

const DEFERRED_TYPES: { [k: string]: string } = {
  "01": "Diferido Cuota Fija con intereses",
  "02": "Diferido con meses de gracia con Intereses",
  "03": "Diferido Pago Mes a Mes con Intereses",
  "04": "Diferido Cuota Fija sin Intereses",
  "05": "Diferido con meses de gracia sin Intereses",
  "06": "Diferido Pago Mes a Mes sin Intereses",
  "07": "Diferido Especial sin Intereses",
  "50": "Diferido Promociones Supermaxi",
  "001": "Diferido corriente",
  "002": "Diferido con intereses",
  "003": "Diferido sin intereses",
  "004": "Tipo de Comercio Gasolinera",
  "007": "Diferido especial con interés",
  "008": "Tarjeta descuento",
  "009": "Diferido especial sin interés",
  "021": "Diferido Plus Cuotas",
  "022": "Diferido Plus",
};

export function updateDeferredOptions(
  state: CardState,
  action: CardAction
): CardState {
  const deferred_options: IDeferredOptionsFetch = action.optionsFetched as IDeferredOptionsFetch;
  const keys: string[] = Object.keys(deferred_options);

  return {
    ...state,
    deferredOptionsComponent: {
      ...state.deferredOptionsComponent,
      deferredTypeSelect: {
        ...state.deferredOptionsComponent.deferredTypeSelect,
        value: keys[0],
        inputItems: keys.map((k: string) => ({
          value: k,
          text: DEFERRED_TYPES[k],
        })),
      },
      monthsSelect: {
        ...state.deferredOptionsComponent.monthsSelect,
        value: deferred_options[keys[0]].months[0].value,
        inputItems: deferred_options[keys[0]].months,
      },
      monthsOfGraceSelect: {
        ...state.deferredOptionsComponent.monthsOfGraceSelect,
        value:
          deferred_options[keys[0]].monthsOfGrace.length > 0
            ? deferred_options[keys[0]].monthsOfGrace[0].value
            : "",
        inputItems: deferred_options[keys[0]].monthsOfGrace,
      },
      optionsFetch: deferred_options,
    },
  };
}

export function UpdateDeferredTypeSelect(
  action: CardAction,
  state: CardState
): CardState {
  const deferred_type: string = action.stringPayload as string;

  return {
    ...state,
    deferredOptionsComponent: {
      ...state.deferredOptionsComponent,
      deferredTypeSelect: {
        ...state.deferredOptionsComponent.deferredTypeSelect,
        value: deferred_type,
      },
      monthsSelect: {
        ...state.deferredOptionsComponent.monthsSelect,
        value:
          state.deferredOptionsComponent.optionsFetch[deferred_type].months[0]
            .value,
        inputItems:
          state.deferredOptionsComponent.optionsFetch[deferred_type].months,
      },
      monthsOfGraceSelect: {
        ...state.deferredOptionsComponent.monthsOfGraceSelect,
        value:
          state.deferredOptionsComponent.optionsFetch[deferred_type]
            .monthsOfGrace.length > 0
            ? state.deferredOptionsComponent.optionsFetch[deferred_type]
                .monthsOfGrace[0].value
            : "",
        inputItems:
          state.deferredOptionsComponent.optionsFetch[deferred_type]
            .monthsOfGrace,
      },
    },
  };
}

export function UpdateDeferredOptionsValidation(state: CardState): CardState {
  return {
    ...state,
    deferredOptionsComponent: {
      ...state.deferredOptionsComponent,
      valid: !state.deferredOptionsComponent.valid,
    },
  };
}

export function UpdateEmailValidity(
  state: CardState,
  action: CardAction
): CardState {
  const valid: boolean = action.emailValid as boolean;

  return {
    ...state,
    email: {
      ...state.email,
      value: "",
      valid: valid,
      rules: {
        ...state.email.rules,
        required: !valid,
      },
      attributes: {
        ...state.email.attributes,
      },
    },
  };
}

export function ResetDeferredOptions(state: CardState): CardState {
  return {
    ...state,
    deferredOptionsComponent: {
      ...state.deferredOptionsComponent,
      deferredTypeSelect: {
        ...state.deferredOptionsComponent.deferredTypeSelect,
        value: "",
        inputItems: [],
      },
      monthsSelect: {
        ...state.deferredOptionsComponent.monthsSelect,
        value: "",
        inputItems: [],
      },
      monthsOfGraceSelect: {
        ...state.deferredOptionsComponent.monthsOfGraceSelect,
        value: "",
        inputItems: [],
      },
      optionsFetch: {},
    },
  };
}

export function UpdateDeferredValue(
  action: CardAction,
  state: CardState
): CardState {
  const selectProp: ISelectProps = action.select as ISelectProps;

  return {
    ...state,
    deferredOptionsComponent: {
      ...state.deferredOptionsComponent,
      [selectProp.id]: selectProp,
    },
  };
}

export function UpdateCardInput(
  action: CardAction,
  state: CardState
): CardState {
  const input: IInputProps = action.input as IInputProps;

  return {
    ...state,
    [input.attributes.id]: input,
  };
}

export function UpdateCardInputDirty(
  action: CardAction,
  state: CardState
): CardState {
  const input: IInputProps = action.input as IInputProps;

  return {
    ...state,
    [input.attributes.id]: {
      ...state[input.attributes.id],
      dirty: true,
    },
  };
}

export function UpdateCardValidation(state: CardState): CardState {
  return {
    ...state,
    isValid: !state.isValid,
  };
}

export function UpdateDeferredState(state: CardState): CardState {
  return {
    ...state,
    isDeferred: !state.isDeferred,
  };
}

export function UpdateCardToken(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    token: action.stringPayload,
  };
}

export function EnableOptView(state: CardState, action: CardAction): CardState {
  return {
    ...state,
    waitingViewEnabled: action.stringPayload !== undefined,
  };
}

export function UpdateOtpState(
  state: CardState,
  action: CardAction
): CardState {
  const otpState: { isValid: boolean } | undefined = action.otpStatePayload as {
    isValid: boolean;
  };
  const new_state: boolean | undefined =
    otpState !== undefined ? otpState.isValid : undefined;

  return {
    ...state,
    otpState: new_state,
  };
}

export function UpdateOtpFailMessage(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    failOtpMessage: action.stringPayload,
  };
}

export function UpdateSecureServiceId(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    secureServiceId: action.stringPayload,
  };
}

export function UpdateOtpType(state: CardState, action: CardAction): CardState {
  return {
    ...state,
    otpType: action.stringPayload,
  };
}

export function UpdateOtpValue(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    otpValue: action.stringPayload,
  };
}

export function DisableOtpView(state: CardState): CardState {
  return {
    ...state,
    waitingViewEnabled: false,
  };
}

export function EnableOtpError(state: CardState): CardState {
  return {
    ...state,
    showOtpError: true,
  };
}

// istanbul ignore next
export function DisableOtpError(state: CardState): CardState {
  return {
    ...state,
    showOtpError: false,
  };
}

export function Refresh(state: CardState): CardState {
  return {
    ...state,
    update: !state.update,
  };
}
export function deleteOtpFailMessage(state: CardState): CardState {
  return {
    ...state,
    failOtpMessage: undefined,
  };
}

export function UpdateNestedMessage(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    nestedOtpMessage: action.stringPayload,
  };
}

export function UpdateNestedErrorCode(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    nestedErrorCode: action.stringPayload,
  };
}

export function UpdateBrandCard(
  state: CardState,
  action: CardAction
): CardState {
  const new_brand: BinInfoResponse = action.cardType as BinInfoResponse;

  return {
    ...state,
    brand: new_brand.brand,
    cardType: new_brand.cardType,
    bank: new_brand.bank,
  };
}

export function UpdateBrandsLogos(
  state: CardState,
  action: CardAction
): CardState {
  const list_brands_logos: GetBrandsLogosByMerchantResponse[] = action.listBrandsLogosPayload as GetBrandsLogosByMerchantResponse[];

  return {
    ...state,
    listBrandsLogosMerchant: list_brands_logos,
  };
}

export function SetCommissionConfig(
  state: CardState,
  action: CardAction
): CardState {
  const commissionConfig: ICommissionConfig = action.commissionConfiguration as ICommissionConfig;

  return {
    ...state,
    commissionConfig,
  };
}

export function SetCommissionFlag(
  state: CardState,
  action: CardAction
): CardState {
  const hasCommission: boolean = action.hasCommission as boolean;

  return {
    ...state,
    hasCommission,
  };
}

export function SetShowCommissionsDetail(
  state: CardState,
  action: CardAction
): CardState {
  const showCommissionsDetail: boolean = action.showCommissionsDetail as boolean;

  return {
    ...state,
    showCommissionsDetail: showCommissionsDetail,
  };
}

export function EnableVoucherView(state: CardState): CardState {
  return {
    ...state,
    showVoucher: true,
  };
}

export function DisableVoucherView(state: CardState): CardState {
  return {
    ...state,
    showVoucher: false,
  };
}

export function UpdateAmountPerMonth(
  state: CardState,
  action: CardAction
): CardState {
  return {
    ...state,
    amountPerMonth: `${action.stringPayload}`,
  };
}

export function EnableTokenWithoutCvv(state: CardState): CardState {
  return {
    ...state,
    cardCVC: {
      ...state.cardCVC,
      valid: true,
    },
  };
}

export function SetExceededRetries(state: CardState): CardState {
  return {
    ...state,
    exceededRetries: true,
  };
}



// WEBPACK FOOTER //
// ./src/containers/Card/store/Handlers.tsx